import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { LongExpiredToggle, QtyStepper, RenewFlag, SearchSelect, StatusBadge, announceRequest } from '@/components/csp/CspKit';
import { useCreateCspRequest, useCspMe } from '@/api/csp';
import { canReduce, exportCsv, fmtDate, sortRows, useCspRows, type CspRow, type CspSort, type CspStatus } from '@/lib/csp';
import { cn } from '@/lib/utils';
import { EmptyDataset, SortTh } from './CspDashboard';

/**
 * CSP → Licences. Every subscription, filterable by client and status. Seats can be adjusted
 * inline: approvers purchase straight away, everyone else raises a request for approval.
 * NCE only allows seat reductions within 7 days after the renewal date, so "−" is disabled
 * outside that window.
 */
export default function CspLicencesPage() {
  const { query, dataset, visible, prefs } = useCspRows({ scoped: true });
  const [params, setParams] = useSearchParams();
  const [client, setClient] = useState('');
  const [status, setStatus] = useState<CspStatus | ''>('');
  const [sort, setSort] = useState<CspSort>({ key: 'days', dir: 'asc' });

  // Pre-filtered views (?client=&status=) from the dashboard / customers / menu — applied, then dropped.
  useEffect(() => {
    if (!params.has('client') && !params.has('status')) return;
    setClient(params.get('client') ?? '');
    setStatus((params.get('status') as CspStatus) ?? '');
    setParams({}, { replace: true });
  }, [params, setParams]);

  const customerOptions = useMemo(() => [...new Set(visible.map((r) => r.customer))].sort().map((c) => ({ value: c, label: c })), [visible]);
  const rows = useMemo(() => {
    const s = client.trim().toLowerCase();
    return sortRows(visible.filter((r) =>
      (!s || r.customer.toLowerCase().includes(s) || r.plan.toLowerCase().includes(s) || r.accountManager.toLowerCase().includes(s))
      && (!status || r.status === status)), sort);
  }, [visible, client, status, sort]);

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
      <PageHeader title="Licences" count={dataset ? rows.length : null} isFetching={query.isFetching}
        subtitle={`Expiring soon = within ${prefs.threshold} days`}
        actions={<>
          <SearchSelect className="w-64" options={customerOptions} value={client} allowFree allOption="All clients"
            placeholder="All clients — type to search…" onChange={(v) => setClient(v)} />
          <select value={status} onChange={(e) => setStatus(e.target.value as CspStatus | '')}
            className={cn('h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground', status && 'bg-secondary')}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="expiring">Expiring soon</option>
            <option value="expired">Expired</option>
          </select>
          <LongExpiredToggle />
          <Button variant="outline" size="sm" onClick={() => exportCsv(rows)} disabled={!rows.length}>
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export
          </Button>
        </>}
      />

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : !dataset ? <EmptyDataset /> : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <SortTh k="customer" sort={sort} setSort={setSort}>Customer</SortTh>
                <SortTh k="plan" sort={sort} setSort={setSort}>Plan</SortTh>
                <SortTh k="quantity" sort={sort} setSort={setSort}>Seats</SortTh>
                <SortTh k="commitment" sort={sort} setSort={setSort}>Term</SortTh>
                <SortTh k="autorenew" sort={sort} setSort={setSort}>Renew</SortTh>
                <SortTh k="status" sort={sort} setSort={setSort}>Status</SortTh>
                <SortTh k="days" sort={sort} setSort={setSort}>Renewal</SortTh>
                <SortTh k="days" sort={sort} setSort={setSort} right>Days left</SortTh>
                <th className="w-[1%] px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No matching licences.</td></tr>}
              {rows.map((r) => <LicenceRow key={r.key} r={r} threshold={prefs.threshold} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LicenceRow({ r, threshold }: { r: CspRow; threshold: number }) {
  const me = useCspMe();
  const create = useCreateCspRequest();
  const [qty, setQty] = useState(r.quantity);
  useEffect(() => { setQty(r.quantity); }, [r.quantity]);
  const delta = qty - r.quantity;
  const approver = !!me.data?.isApprover;
  const reducible = canReduce(r);

  async function submit() {
    try {
      const res = await create.mutateAsync({
        customer: r.customer, product: r.plan, term: r.commitment, quantity: qty, previousQuantity: r.quantity,
        type: 'adjustment', autoApprove: approver, notes: `Seat adjustment via Cosmos Licences (${r.quantity} → ${qty})`,
      });
      announceRequest(res);
      setQty(r.quantity); // the stored quantity only changes with the next Partner Center upload
    } catch (e) {
      toast.error(`Could not submit: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  }

  return (
    <tr className="hover:bg-muted/30">
      <td className="max-w-[200px] px-3 py-2">
        <div className="truncate font-medium text-foreground" title={r.customer}>{r.customer}</div>
        {r.accountManager && <div className="truncate text-[11px] text-muted-foreground">{r.accountManager}</div>}
      </td>
      <td className="max-w-[260px] truncate px-3 py-2" title={r.plan}>{r.plan}</td>
      <td className="px-3 py-2">
        <QtyStepper size="sm" value={qty} onChange={setQty} min={reducible ? 0 : r.quantity}
          minusDisabled={!reducible && qty <= r.quantity}
          minusTitle={reducible ? 'Reduce seats' : 'Seat reductions are only allowed within 7 days after the renewal date'} />
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground">{r.commitment || '—'}</td>
      <td className="px-3 py-2"><RenewFlag on={r.autoRenew} /></td>
      <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fmtDate(r.endDate)}</td>
      <td className={cn('px-3 py-2 text-right font-semibold tabular-nums',
        r.daysLeft == null ? 'text-muted-foreground' : r.daysLeft < 0 ? 'text-destructive' : r.daysLeft <= threshold ? 'text-warning' : 'text-foreground')}>
        {r.daysLeft ?? '—'}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        {delta !== 0 && (
          <Button size="sm" variant={approver ? 'default' : 'secondary'} className="h-7 text-xs" onClick={submit} disabled={create.isPending}>
            {create.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
            {approver ? 'Purchase' : 'Request'} ({delta > 0 ? '+' : ''}{delta})
          </Button>
        )}
      </td>
    </tr>
  );
}
